/**
 * HealthStatusCard - Service health indicator with status visualization
 *
 * Features:
 * - Animated status indicator with pulse effect
 * - Latency display with color-coded thresholds
 * - Last check timestamp
 * - Expandable error details
 * - Smooth hover animations
 */
import { useState } from 'react';
import { cn } from '@respectly/ui';
import { Card, CardContent } from '@respectly/ui';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Server,
  Database,
  Zap,
  Cloud,
  AlertCircle,
  ChevronDown,
  Clock,
  Activity,
} from 'lucide-react';
import type { HealthStatus, ServiceHealth } from '@respectly/shared-types';

// ============================================
// Types
// ============================================

interface HealthStatusCardProps {
  service: ServiceHealth;
  className?: string;
}

// ============================================
// Constants
// ============================================

const STATUS_CONFIG: Record<HealthStatus, { color: string; bgColor: string; pulseColor: string; label: string }> = {
  healthy: {
    color: 'text-emerald-600 dark:text-emerald-400',
    bgColor: 'bg-emerald-500',
    pulseColor: 'bg-emerald-400',
    label: 'Healthy',
  },
  degraded: {
    color: 'text-amber-600 dark:text-amber-400',
    bgColor: 'bg-amber-500',
    pulseColor: 'bg-amber-400',
    label: 'Degraded',
  },
  down: {
    color: 'text-red-600 dark:text-red-400',
    bgColor: 'bg-red-500',
    pulseColor: 'bg-red-400',
    label: 'Down',
  },
};

const SERVICE_ICONS: Record<string, React.ElementType> = {
  'api-gateway': Server,
  database: Database,
  redis: Zap,
  sinch: Cloud,
  mailgun: Cloud,
  ultravox: Cloud,
  stripe: Cloud,
};

// ============================================
// Helpers
// ============================================

function formatLatency(ms: number | null | undefined): string {
  if (ms === null || ms === undefined) return '—';
  if (ms < 1000) return `${Math.round(ms)}ms`;
  return `${(ms / 1000).toFixed(2)}s`;
}

function getLatencyColor(ms: number | null | undefined): string {
  if (ms === null || ms === undefined) return 'text-slate-400';
  if (ms < 100) return 'text-emerald-600 dark:text-emerald-400';
  if (ms < 500) return 'text-amber-600 dark:text-amber-400';
  return 'text-red-600 dark:text-red-400';
}

function formatLastCheck(date: Date): string {
  const now = new Date();
  const diff = now.getTime() - new Date(date).getTime();
  const seconds = Math.floor(diff / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);

  if (seconds < 60) return 'Just now';
  if (minutes < 60) return `${minutes}m ago`;
  if (hours < 24) return `${hours}h ago`;
  return new Date(date).toLocaleDateString();
}

function getServiceDisplayName(service: string): string {
  const names: Record<string, string> = {
    'api-gateway': 'API Gateway',
    database: 'Database',
    redis: 'Redis Cache',
    sinch: 'Sinch SMS',
    mailgun: 'Mailgun',
    ultravox: 'Ultravox AI',
    stripe: 'Stripe',
  };
  return names[service] || service;
}

// ============================================
// Component
// ============================================

export function HealthStatusCard({ service, className }: HealthStatusCardProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const config = STATUS_CONFIG[service.status];
  const Icon = SERVICE_ICONS[service.service] || Server;
  const hasError = service.status !== 'healthy' && service.errorMessage;

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      whileHover={{ y: -2 }}
      className={className}
    >
      <Card
        className={cn(
          'relative overflow-hidden border-slate-200/60 dark:border-slate-700/60',
          'bg-white dark:bg-slate-900 shadow-sm hover:shadow-md transition-shadow duration-300',
          hasError && 'cursor-pointer'
        )}
        onClick={() => hasError && setIsExpanded(!isExpanded)}
      >
        {/* Status accent bar */}
        <div
          className={cn(
            'absolute inset-x-0 top-0 h-1',
            service.status === 'healthy' && 'bg-gradient-to-r from-emerald-400 to-emerald-500',
            service.status === 'degraded' && 'bg-gradient-to-r from-amber-400 to-amber-500',
            service.status === 'down' && 'bg-gradient-to-r from-red-400 to-red-500'
          )}
        />

        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            {/* Service Icon */}
            <div
              className={cn(
                'flex-shrink-0 w-10 h-10 rounded-lg flex items-center justify-center',
                service.status === 'healthy' && 'bg-emerald-50 dark:bg-emerald-900/20',
                service.status === 'degraded' && 'bg-amber-50 dark:bg-amber-900/20',
                service.status === 'down' && 'bg-red-50 dark:bg-red-900/20'
              )}
            >
              <Icon className={cn('h-5 w-5', config.color)} />
            </div>

            {/* Content */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between gap-2">
                <h3 className="font-semibold text-slate-900 dark:text-white truncate">
                  {getServiceDisplayName(service.service)}
                </h3>
                {/* Status Indicator */}
                <div className="flex items-center gap-2">
                  <div className="relative flex items-center justify-center">
                    {/* Pulse animation for non-healthy states */}
                    {service.status !== 'healthy' && (
                      <span
                        className={cn(
                          'absolute inline-flex h-3 w-3 rounded-full opacity-75 animate-ping',
                          config.pulseColor
                        )}
                      />
                    )}
                    <span
                      className={cn(
                        'relative inline-flex h-2.5 w-2.5 rounded-full',
                        config.bgColor
                      )}
                    />
                  </div>
                  <span className={cn('text-xs font-medium', config.color)}>
                    {config.label}
                  </span>
                </div>
              </div>

              {/* Metrics Row */}
              <div className="flex items-center gap-4 mt-2">
                {/* Latency */}
                <div className="flex items-center gap-1.5">
                  <Activity className="h-3.5 w-3.5 text-slate-400" />
                  <span
                    className={cn(
                      'text-sm font-medium tabular-nums',
                      getLatencyColor(service.latencyMs)
                    )}
                  >
                    {formatLatency(service.latencyMs)}
                  </span>
                </div>

                {/* Last Check */}
                <div className="flex items-center gap-1.5">
                  <Clock className="h-3.5 w-3.5 text-slate-400" />
                  <span className="text-sm text-slate-500 dark:text-slate-400">
                    {formatLastCheck(service.lastCheck)}
                  </span>
                </div>

                {/* Expand indicator for errors */}
                {hasError && (
                  <motion.div
                    animate={{ rotate: isExpanded ? 180 : 0 }}
                    transition={{ duration: 0.2 }}
                    className="ml-auto"
                  >
                    <ChevronDown className="h-4 w-4 text-slate-400" />
                  </motion.div>
                )}
              </div>
            </div>
          </div>

          {/* Error Details (Expandable) */}
          <AnimatePresence>
            {isExpanded && hasError && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.2 }}
                className="overflow-hidden"
              >
                <div className="mt-3 pt-3 border-t border-slate-200 dark:border-slate-700">
                  <div className="flex items-start gap-2 text-sm">
                    <AlertCircle className="h-4 w-4 text-red-500 flex-shrink-0 mt-0.5" />
                    <p className="text-slate-600 dark:text-slate-400">
                      {service.errorMessage}
                    </p>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </CardContent>
      </Card>
    </motion.div>
  );
}

// ============================================
// Skeleton
// ============================================

export function HealthStatusCardSkeleton({ className }: { className?: string }) {
  return (
    <Card className={cn('border-slate-200/60 dark:border-slate-700/60', className)}>
      <CardContent className="p-4">
        <div className="flex items-start gap-3 animate-pulse">
          <div className="w-10 h-10 rounded-lg bg-slate-200 dark:bg-slate-700" />
          <div className="flex-1">
            <div className="flex items-center justify-between gap-2">
              <div className="h-5 w-24 bg-slate-200 dark:bg-slate-700 rounded" />
              <div className="flex items-center gap-2">
                <div className="h-2.5 w-2.5 rounded-full bg-slate-200 dark:bg-slate-700" />
                <div className="h-4 w-12 bg-slate-200 dark:bg-slate-700 rounded" />
              </div>
            </div>
            <div className="flex items-center gap-4 mt-2">
              <div className="h-4 w-16 bg-slate-200 dark:bg-slate-700 rounded" />
              <div className="h-4 w-20 bg-slate-200 dark:bg-slate-700 rounded" />
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
